import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, MapPin, FileText, User, Settings, LogOut, UserCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Button from './Button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin: userIsAdmin } = useAuth();
  const isAdmin = location.pathname.startsWith('/admin');

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setShowUserMenu(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowUserMenu(false);
    }, 250); // 0.25 second delay
    setHoverTimeout(timeout);
  };



  const citizenLinks = [
    { to: '/', label: 'Map View', icon: MapPin },
    { to: '/report', label: 'Report Issue', icon: FileText },
    { to: '/my-reports', label: 'My Reports', icon: User },
  ];

  const adminLinks = [
    { to: '/admin', label: 'Dashboard', icon: Settings },
    { to: '/admin/reports', label: 'All Reports', icon: FileText },
    { to: '/admin/analytics', label: 'Analytics', icon: MapPin },
  ];

  const links = userIsAdmin() ? adminLinks : citizenLinks;

  return (
    <nav className="gradient-bg text-white shadow-large relative">
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-secondary-600/20"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to={userIsAdmin() ? "/admin" : "/"} className="flex items-center space-x-3 group">
              <div className="p-2 bg-white/20 rounded-xl group-hover:bg-white/30 transition-all duration-300">
                <MapPin className="h-6 w-6" />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">Civic Watch</span>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {user && links.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  location.pathname === to
                    ? 'bg-white/20 text-white shadow-soft backdrop-blur-sm'
                    : 'text-blue-100 hover:bg-white/10 hover:text-white hover:shadow-soft'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            ))}

            
            {user ? (
              <div 
                className="relative z-[100]"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2.5 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300 focus:outline-none backdrop-blur-sm border border-white/25 text-white"
                >
                  <UserCircle className="h-5 w-5 text-sky-200" />
                  <span className="text-sm font-semibold truncate max-w-[130px]">
                    {user.name || user.username || user.email?.split('@')[0] || 'Account'}
                  </span>
                </button>
                
                {showUserMenu && (
                  <div 
                    className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl py-1 text-slate-800 animate-scale-up"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      zIndex: 99999,
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.75rem',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}
                  >
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80 rounded-t-xl">
                      <div className="font-bold text-slate-900 text-sm truncate">
                        {user.name || user.username || 'User'}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">{user.email}</div>
                      <div className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800">
                        {userIsAdmin() ? 'Admin' : 'Citizen'}
                      </div>
                    </div>

                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center w-full px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <User className="h-4 w-4 mr-2.5 text-sky-600" />
                      <span>Profile & Settings</span>
                    </Link>

                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                        navigate('/login');
                      }}
                      className="flex items-center w-full px-4 py-2.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-2.5 text-red-500" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <button className="px-4 py-2 text-sm font-medium text-white/90 hover:text-white transition-colors">Sign In</button>
                </Link>
                <Link to="/register">
                  <button className="btn-gradient px-6 py-2 rounded-xl text-sm font-medium">Sign Up</button>
                </Link>
              </div>
            )}
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-blue-100 hover:text-white focus:outline-none focus:text-white"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden">
          <div className="px-4 pt-4 pb-6 space-y-2 bg-gradient-to-b from-primary-700/90 to-secondary-700/90 backdrop-blur-sm">
            {user && links.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === to
                    ? 'bg-blue-800 text-white'
                    : 'text-blue-100 hover:bg-blue-600 hover:text-white'
                }`}
                onClick={() => setIsOpen(false)}
              >
                <Icon className="h-5 w-5" />
                <span>{label}</span>
              </Link>
            ))}

            
            {user ? (
              <div className="border-t border-blue-600 pt-3 mt-3">
                <div className="px-3 py-2 text-blue-100">
                  <div className="font-medium">{user.name || user.username || 'User'}</div>
                  <div className="text-sm text-blue-200">{user.email}</div>
                </div>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 w-full px-3 py-2 text-blue-100 hover:bg-blue-600 rounded-md text-base font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="h-5 w-5" />
                  <span>Profile & Settings</span>
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                    navigate('/login');
                  }}
                  className="flex items-center space-x-2 w-full px-3 py-2 text-blue-100 hover:bg-blue-600 rounded-md text-base font-medium"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign out</span>
                </button>
              </div>
            ) : (
              <div className="border-t border-blue-600 pt-3 mt-3 space-y-2">
                <Link
                  to="/login"
                  className="flex items-center space-x-2 px-3 py-2 text-blue-100 hover:bg-blue-600 rounded-md text-base font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="h-5 w-5" />
                  <span>Sign In</span>
                </Link>
                <Link
                  to="/register"
                  className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-400 px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  <UserCircle className="h-5 w-5" />
                  <span>Sign Up</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;